import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, Card } from "@/components/common";
import { toast } from "sonner";
import { Swords, Dumbbell, UserSearch, UserRound, MapPin, Star, ArrowRight, Radio, Sparkles } from "lucide-react";

const QUICK = [
  { label: "Find a match", icon: Swords, to: "/app/matches", tint: "bg-orange-50 text-orange-600" },
  { label: "Book a ground", icon: Dumbbell, to: "/app/facilities", tint: "bg-emerald-50 text-emerald-600" },
  { label: "Find players", icon: UserSearch, to: "/app/players", tint: "bg-sky-50 text-sky-600" },
  { label: "Edit profile", icon: UserRound, to: "/app/onboarding", tint: "bg-violet-50 text-violet-600" },
];

export default function IndividualDashboard() {
  const { user } = useAuth();
  const [athlete, setAthlete] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () => api.get("/me/athlete").then((r) => setAthlete(r.data || {})).catch(() => setAthlete({}));
  useEffect(() => {
    load();
    api.get("/public/matches").then((r) => setMatches(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (athlete === null) return;
    const city = athlete.city ? `?city=${encodeURIComponent(athlete.city)}&limit=4` : "?limit=4";
    api.get(`/public/facilities${city}`).then((r) => setFacilities(r.data || [])).catch(() => {}).finally(() => setLoaded(true));
  }, [athlete]);

  const toggleOpen = async () => {
    try { const { data } = await api.post("/me/athlete", { open_to_join: !athlete.open_to_join }); setAthlete(data); toast.success(data.open_to_join ? "You're now open to join teams" : "Hidden from team search"); }
    catch { toast.error("Could not update"); }
  };

  if (athlete === null || !loaded) return <Spinner />;
  const sports = athlete.sports?.length ? athlete.sports : (athlete.sport ? [athlete.sport] : []);
  const needsProfile = !athlete.onboarded || sports.length === 0;

  return (
    <div className="animate-fadeUp">
      <PageHeader title={`Welcome, ${user?.name?.split(" ")[0] || "player"}`} subtitle="Your game, personalized" />

      {needsProfile && (
        <Card className="mb-5 border-primary/40 bg-primary/5">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-primary" />
              <p className="text-sm"><span className="font-semibold">Finish your profile</span> to get matches and grounds tailored to you.</p>
            </div>
            <Link to="/app/onboarding" data-testid="complete-profile-cta" className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-[#FF8533]">Complete profile</Link>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {sports.map((s) => <span key={s} className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium capitalize text-primary">{s}</span>)}
        {athlete.city && <span className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {athlete.city}</span>}
        <button onClick={toggleOpen} data-testid="open-to-join-toggle"
          className={`ml-auto inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium transition-colors ${
            athlete.open_to_join ? "border-emerald-500/40 bg-emerald-50 text-emerald-700" : "border-border bg-background text-muted-foreground"}`}>
          <span className={`h-2 w-2 rounded-full ${athlete.open_to_join ? "bg-emerald-500" : "bg-muted-foreground"}`} /> Open to join teams
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {QUICK.map((q) => {
          const Icon = q.icon;
          return (
            <Link key={q.label} to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/ /g, "-")}`}
              className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-card p-4 transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-lg">
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${q.tint}`}><Icon className="h-5 w-5" /></span>
              <span className="font-display text-base font-semibold">{q.label}</span>
            </Link>
          );
        })}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-xl font-semibold">{athlete.city ? `Grounds in ${athlete.city}` : "Featured grounds"}</h3>
            <Link to="/app/facilities" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">All <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          {facilities.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No grounds found yet.</p> : (
            <div className="space-y-3">
              {facilities.map((f) => (
                <Link key={f.id} to={`/app/facilities/${f.id}`} data-testid={`home-facility-${f.id}`} className="flex items-center gap-3 rounded-xl border border-border p-2.5 transition-colors hover:border-primary/40">
                  <img src={f.image_url || "https://images.unsplash.com/photo-1556056504-5c7696c4c28d?w=160"} alt={f.name} className="h-14 w-14 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{f.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{f.sport} · {f.city}</p>
                  </div>
                  {f.rating != null && <span className="flex items-center gap-1 text-xs font-semibold text-primary"><Star className="h-3 w-3 fill-primary" /> {f.rating}</span>}
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-xl font-semibold">Matches to watch</h3>
            <Link to="/app/matches" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">All <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          {matches.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No matches scheduled yet.</p> : (
            <div className="space-y-3">
              {matches.slice(0, 4).map((m) => (
                <Link key={m.id} to="/app/matches" className="block rounded-xl border border-border p-3 transition-colors hover:border-primary/40">
                  <div className="flex items-center justify-between">
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold capitalize text-primary">{m.sport}</span>
                    {m.is_live && <span className="inline-flex items-center gap-1 rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-bold text-red-600"><Radio className="h-3 w-3" /> LIVE</span>}
                  </div>
                  <p className="mt-1.5 truncate text-sm font-semibold">{m.title}</p>
                  <p className="text-xs text-muted-foreground">{m.venue}{m.date ? ` · ${m.date}` : ""}</p>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
